/**
 * Shared manual-override edit panel for a tenant/booking, open to admin and
 * super_admin (never tech — see canManageTenant in @/lib/roles).
 *
 * Extracted from TenantDetail.tsx so the full-page tenant route and the
 * redesigned tenant profile modal (TenantProfileDialog) share one edit
 * implementation. Every change is audit-logged (audit_logs, source
 * admin_manual_override) and mirrored onto both bookings.* and
 * profiles.* so the various admin surfaces never drift apart.
 */
import { useEffect, useState } from "react";
import { Loader2, Save, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  useUpdateBooking,
  useUpdateProfile,
  type BookingRow,
  type ProfileRow,
} from "@/hooks/v2/useProperties";
import {
  isDepositEditLocked,
  TENANT_JOURNEY_STAGES,
  tenantJourneyStageLabel,
  type TenantJourneyStageId,
} from "@/lib/tenantJourney";
import {
  discountFieldsFromValue,
  EMPTY_DISCOUNT,
  TenantDiscountFields,
  type TenantDiscountValue,
} from "./TenantDiscountFields";
import { repriceRentRows, resolveRoomBaseSync } from "@/lib/rent-room-sync";
import { effectiveDepositEUR, type DiscountMode } from "@/lib/rent-discount";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { NationalityCombobox } from "@/components/booking/NationalityCombobox";
import { isUnderMinimumAge, latestAdultBirthDate } from "@/lib/adult-age";

function getContractStatuses(t: (key: string, fallback: string) => string) {
  return [
    {
      value: "not_ready",
      label: t("admin.tenantEdit.contractNotReady", "Not ready"),
    },
    // Generated, reviewable, deliberately NOT released to the tenant.
    {
      value: "draft",
      label: t("admin.tenantEdit.contractDraft", "Draft (not sent)"),
    },
    {
      value: "ready_to_sign",
      label: t("admin.tenantEdit.contractReadyToSign", "Ready to sign"),
    },
    { value: "signed", label: t("admin.tenantEdit.contractSigned", "Signed") },
    {
      value: "approved",
      label: t("admin.tenantEdit.contractApproved", "Approved"),
    },
  ] as const;
}

function getPaymentStatuses(t: (key: string, fallback: string) => string) {
  return [
    {
      value: "pending",
      label: t("admin.tenantEdit.paymentPending", "Pending"),
    },
    { value: "paid", label: t("admin.tenantEdit.paymentPaid", "Paid") },
    {
      value: "overdue_1",
      label: t("admin.tenantEdit.paymentOverdue1", "Overdue (stage 1)"),
    },
    {
      value: "overdue_2",
      label: t("admin.tenantEdit.paymentOverdue2", "Overdue (stage 2)"),
    },
    {
      value: "overdue_3",
      label: t("admin.tenantEdit.paymentOverdue3", "Overdue (stage 3)"),
    },
    {
      value: "inkasso",
      label: t("admin.tenantEdit.paymentInkasso", "Inkasso"),
    },
  ] as const;
}

/** Form state for the discount already stored on the booking, if any. */
function discountValueFromBooking(b: BookingRow): TenantDiscountValue {
  const rentValue = b.rent_discount_value;
  const depositValue = b.deposit_discount_value;
  const months = b.rent_discount_months;
  const hasAny = Number(rentValue ?? 0) > 0 || Number(depositValue ?? 0) > 0;
  if (!hasAny) return EMPTY_DISCOUNT;
  return {
    enabled: true,
    rentMode: (b.rent_discount_mode as DiscountMode) ?? "eur",
    rentAmount: rentValue != null ? String(rentValue) : "",
    months: months != null ? String(months) : "3",
    fullTerm: months == null,
    depositMode: (b.deposit_discount_mode as DiscountMode) ?? "eur",
    depositAmount: depositValue != null ? String(depositValue) : "",
  };
}

export function TenantEditPanel({
  booking,
  profile,
  currentUserId,
  currentStageId,
  stageBusy,
  onForceStage,
  onClose,
  onSaved,
  isSuperAdmin = false,
}: {
  booking: BookingRow;
  profile: ProfileRow | null;
  currentUserId: string | null;
  /** Live derived journey stage — drives the Booking status picker's value. */
  currentStageId: TenantJourneyStageId;
  /** A lifecycle mutation is in flight (disables the picker). */
  stageBusy: boolean;
  /** Force the tenant to an exact journey stage (writes the underlying records). */
  onForceStage: (stage: TenantJourneyStageId) => void;
  onClose: () => void;
  /**
   * A save landed — the host must refetch.
   *
   * The two mutations invalidate the booking and profile rows themselves, but
   * the audit entry this panel writes afterwards belongs to a query no
   * mutation knows about, so the Activity Timeline kept showing the state from
   * before the edit until the page was reloaded. The host owns the refresh
   * policy (it has one for its own actions), so it is told rather than second-
   * guessed here.
   */
  onSaved?: () => void;
  /**
   * Unlocks the commercial half of the panel: contract status, payment status
   * (which drives dunning), rent, deposit and the move dates. A plain `admin`
   * gets identity, date of birth and booking status only — the panel is shared
   * by both tiers rather than duplicated, so the split lives here.
   */
  isSuperAdmin?: boolean;
}) {
  const { t, language } = useLanguage();
  const CONTRACT_STATUSES = getContractStatuses(t);
  const PAYMENT_STATUSES = getPaymentStatuses(t);
  const [name, setName] = useState(
    profile?.full_name ?? profile?.name ?? booking.name ?? ""
  );
  const [email, setEmail] = useState(profile?.email ?? booking.email ?? "");
  const [phone, setPhone] = useState(profile?.phone ?? booking.phone ?? "");
  const [dob, setDob] = useState(profile?.date_of_birth ?? "");
  // COL-530: how existing tenants get a gender. There is nothing to infer it
  // from, so this field IS the backfill — the public room page shows whatever
  // has been filled in here and an empty dot for everyone else.
  const [gender, setGender] = useState(profile?.gender ?? "");
  // COL-618: nationality ("" = unknown) lives on the profile, like gender.
  const [nationality, setNationality] = useState(profile?.nationality ?? "");
  // COL-618: whether the Jobcenter pays the rent — per application, so on the
  // booking. "unset" is a real third state (never asked), distinct from "no".
  const [jobcenter, setJobcenter] = useState<"unset" | "yes" | "no">(
    booking.jobcenter_pays_rent == null
      ? "unset"
      : booking.jobcenter_pays_rent
        ? "yes"
        : "no"
  );
  const [contractStatus, setContractStatus] = useState(
    booking.contract_status ?? ""
  );
  const [paymentStatus, setPaymentStatus] = useState(
    booking.payment_status ?? ""
  );
  const [monthlyRent, setMonthlyRent] = useState(
    booking.monthly_rent != null ? String(booking.monthly_rent) : ""
  );
  const [depositAmount, setDepositAmount] = useState(
    booking.deposit_amount != null ? String(booking.deposit_amount) : ""
  );
  // Tenant-specific discount + the reason for the change. The reason is
  // mandatory whenever anything in this panel changed (COL-360) — it is what
  // the activity timeline shows next to the diff, so an unexplained rent
  // change cannot be saved at all.
  const [discount, setDiscount] = useState<TenantDiscountValue>(() =>
    discountValueFromBooking(booking)
  );
  const [reason, setReason] = useState("");
  const [plannedMoveIn, setPlannedMoveIn] = useState(
    booking.move_in_date ?? ""
  );
  const [actualMoveIn, setActualMoveIn] = useState(
    booking.actual_move_in_date ?? ""
  );
  const [plannedMoveOut, setPlannedMoveOut] = useState(
    booking.move_out_date ?? ""
  );
  const [actualMoveOut, setActualMoveOut] = useState(
    booking.actual_move_out_date ?? ""
  );

  // Sync profile fields when profile loads (fetched asynchronously after booking)
  useEffect(() => {
    setName(profile?.full_name ?? profile?.name ?? booking.name ?? "");
    setEmail(profile?.email ?? booking.email ?? "");
    setPhone(profile?.phone ?? booking.phone ?? "");
    setDob(profile?.date_of_birth ?? "");
    setGender(profile?.gender ?? "");
    setNationality(profile?.nationality ?? "");
  }, [profile, booking.name, booking.email, booking.phone]);

  const updateBooking = useUpdateBooking(booking.id);
  const updateProfile = useUpdateProfile(profile?.id ?? null);

  // Deposit edits close once the deposit has been settled — see
  // isDepositEditLocked for why the expected-vs-paid comparison depends on it.
  const depositLocked = isDepositEditLocked(currentStageId);

  /**
   * Month a newly saved discount is anchored to. An existing discount keeps its
   * original anchor so editing the amount does not silently shift the window;
   * a new one starts next month, because the current month has usually been
   * invoiced already.
   */
  const discountStartMonth = (() => {
    const existing = (booking as unknown as Record<string, unknown>)
      .rent_discount_start_month;
    if (existing) return String(existing).slice(0, 10);
    const now = new Date();
    const next = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)
    );
    return next.toISOString().slice(0, 10);
  })();

  const handleSave = async () => {
    const bookingPatch: Partial<BookingRow> = {};
    // Reminder-signal fields written alongside a payment_status → "paid"
    // transition (see below). Kept separate because they aren't part of
    // BookingRow's editable shape.
    const reminderSignal: Record<string, unknown> = {};
    // Discount columns are collected separately so the audit row can record
    // exactly what changed; they are merged into the DB update below.
    const discountPatch: Record<string, string | number | null> = {};
    // Booking status / journey stage is NOT part of this batched save — it is a
    // lifecycle action handled by the stage picker (onForceStage), which writes
    // the underlying records directly.
    //
    // The commercial fields below are collected in one branch so a plain admin,
    // whose UI never renders them, cannot submit them either — the state would
    // otherwise still hold their initial values and a future edit to this
    // function could start sending them.
    if (isSuperAdmin) {
      if (contractStatus !== (booking.contract_status ?? ""))
        bookingPatch.contract_status = contractStatus || null;
      if (paymentStatus !== (booking.payment_status ?? "")) {
        bookingPatch.payment_status = paymentStatus || null;
        // The Reminders views ignore the stored `payment_status` and recompute
        // overdue from `last_payment_received_at` / `manually_marked_paid`. So
        // setting status → "paid" here must also write the reminder signal,
        // otherwise the tenant stays on the overdue list, keeps their escalation
        // stage and accrued fees, and keeps receiving dunning emails. Mirror the
        // Reminders "Mark paid" convention (see markPaid in AdminReminders).
        if ((paymentStatus || null) === "paid") {
          reminderSignal.last_payment_received_at = new Date().toISOString();
          reminderSignal.reminder_stage = 0;
          reminderSignal.accumulated_fees_eur = 0;
        }
      }
      if (plannedMoveIn !== (booking.move_in_date ?? ""))
        bookingPatch.move_in_date = plannedMoveIn || null;
      if (actualMoveIn !== (booking.actual_move_in_date ?? "")) {
        bookingPatch.actual_move_in_date = actualMoveIn || null;
        // Recording an actual move-in date IS a move-in confirmation, and the
        // Active journey gate reads `move_in_confirmed_at` — not this column, and
        // not `status`. Without this the tenant would keep sitting at "Handover
        // protocol pending" after the date was typed in. Only ever set (never
        // cleared): the confirmation may also have come from a submitted protocol,
        // which correcting a date must not retract.
        // Interpreted as midnight UTC (not local), matching how the migration
        // backfills this column from `actual_move_in_date` (`date::timestamptz` on
        // a UTC server). A local-time parse would store the previous UTC day for
        // any positive offset and render "Moved in" a day early in the timeline.
        if (actualMoveIn && !booking.move_in_confirmed_at)
          bookingPatch.move_in_confirmed_at = new Date(
            `${actualMoveIn}T00:00:00Z`
          ).toISOString();
      }
      if (plannedMoveOut !== (booking.move_out_date ?? ""))
        bookingPatch.move_out_date = plannedMoveOut || null;
      if (actualMoveOut !== (booking.actual_move_out_date ?? ""))
        bookingPatch.actual_move_out_date = actualMoveOut || null;

      const rentVal = monthlyRent === "" ? null : Number(monthlyRent);
      const depositVal = depositAmount === "" ? null : Number(depositAmount);
      if (rentVal !== (booking.monthly_rent ?? null))
        bookingPatch.monthly_rent = rentVal;
      // A locked deposit is read-only: the tenant has already paid against the
      // stored figure, so it is never part of the patch (COL-360).
      if (!depositLocked && depositVal !== (booking.deposit_amount ?? null))
        bookingPatch.deposit_amount = depositVal;

      // A limited (non-full-term) rent discount needs a real duration.
      // `discountFieldsFromValue` treats a blank/zero/invalid months field the
      // same as "no duration stored", which the resolvers read as full-term —
      // so a cleared field must be rejected here rather than silently turning
      // an intended N-month discount into a permanent one (COL-360).
      if (
        discount.enabled &&
        !discount.fullTerm &&
        !(Number(discount.months) > 0)
      ) {
        toast.error(
          t(
            "admin.tenantEdit.errDiscountDuration",
            "Enter a discount duration in months, or choose Full term."
          )
        );
        return;
      }

      // Discount fields. Written whenever they differ from what is stored, so
      // removing a discount clears them rather than leaving a stale window
      // behind.
      const nextDiscount = discountFieldsFromValue(
        discount,
        rentVal ?? 0,
        depositVal ?? 0,
        discountStartMonth,
        { depositLocked }
      );
      const stored = booking as unknown as Record<string, unknown>;
      // A locked deposit keeps the discount that was already agreed on it.
      // Forcing these to null would erase a settled discount on ANY later save
      // — fixing a phone number would silently raise the expected deposit and
      // could strand a tenant who had already paid the discounted amount.
      const lockedDepositMode = depositLocked
        ? ((stored.deposit_discount_mode as string | null) ?? null)
        : null;
      const lockedDepositValue = depositLocked
        ? ((stored.deposit_discount_value as number | null) ?? null)
        : null;
      const discountCols = {
        rent_discount_mode: discount.enabled
          ? (nextDiscount.rent_discount_mode ?? null)
          : null,
        rent_discount_value: discount.enabled
          ? (nextDiscount.rent_discount_value ?? null)
          : null,
        rent_discount_months: discount.enabled
          ? (nextDiscount.rent_discount_months ?? null)
          : null,
        rent_discount_start_month: discount.enabled
          ? (nextDiscount.rent_discount_start_month ?? null)
          : null,
        deposit_discount_mode: depositLocked
          ? lockedDepositMode
          : discount.enabled
            ? (nextDiscount.deposit_discount_mode ?? null)
            : null,
        deposit_discount_value: depositLocked
          ? lockedDepositValue
          : discount.enabled
            ? (nextDiscount.deposit_discount_value ?? null)
            : null,
      };
      // No amount means no discount — per side. Collapsing only when BOTH are
      // empty would give a deposit-only discount a rent window it never had,
      // which then reappears as a spurious change on every later save.
      if (discountCols.rent_discount_value == null) {
        discountCols.rent_discount_mode = null;
        discountCols.rent_discount_months = null;
        discountCols.rent_discount_start_month = null;
      }
      if (discountCols.deposit_discount_value == null) {
        discountCols.deposit_discount_mode = null;
      }
      for (const [col, next] of Object.entries(discountCols)) {
        const before = stored[col] ?? null;
        const normalizedBefore =
          typeof next === "number" && before != null ? Number(before) : before;
        if (normalizedBefore !== next) {
          discountPatch[col] = next;
        }
      }
    }

    const profilePatch: Partial<ProfileRow> = {};
    if (profile) {
      if (
        name.trim() !== (profile.full_name ?? "") ||
        name.trim() !== (profile.name ?? "")
      ) {
        // Mirror to both columns so the other admin surface (which reads
        // `name`) stays in sync with this one (which reads `full_name`).
        profilePatch.full_name = name.trim() || null;
        profilePatch.name = name.trim() || null;
      }
      if (email.trim() !== (profile.email ?? ""))
        profilePatch.email = email.trim() || null;
      if (phone.trim() !== (profile.phone ?? ""))
        profilePatch.phone = phone.trim() || null;
      if (dob !== (profile.date_of_birth ?? "")) {
        profilePatch.date_of_birth = dob || null;
        // Stamp provenance so the Wohnungsgeberbestätigung flow can tell a
        // typed-in date from one the ID extraction produced.
        profilePatch.date_of_birth_source = dob ? "manual" : null;
      }
      if (gender !== (profile.gender ?? "")) {
        profilePatch.gender = gender || null;
      }
      if (nationality !== (profile.nationality ?? "")) {
        profilePatch.nationality = nationality || null;
      }
    }
    const jobcenterValue = jobcenter === "unset" ? null : jobcenter === "yes";
    if (jobcenterValue !== (booking.jobcenter_pays_rent ?? null)) {
      bookingPatch.jobcenter_pays_rent = jobcenterValue;
    }
    // Always mirror contact onto the booking row too (whether or not a profile
    // is linked) so bookings.* and profiles.* never drift apart.
    if (name.trim() !== (booking.name ?? ""))
      bookingPatch.name = name.trim() || null;
    if (email.trim() !== (booking.email ?? ""))
      bookingPatch.email = email.trim() || null;
    if (phone.trim() !== (booking.phone ?? ""))
      bookingPatch.phone = phone.trim() || null;

    // COL-629: nobody under 18 on a profile. The profiles trigger refuses it
    // too; refusing here names the field instead of failing the whole save.
    if (
      profilePatch.date_of_birth &&
      isUnderMinimumAge(profilePatch.date_of_birth)
    ) {
      toast.error(
        t(
          "admin.tenantEdit.errDobUnderage",
          "The tenant must be at least 18 years old. Check the date of birth."
        )
      );
      return;
    }

    const hasBP = Object.keys(bookingPatch).length > 0;
    const hasPP = Object.keys(profilePatch).length > 0;
    const hasDP = Object.keys(discountPatch).length > 0;

    if (!hasBP && !hasPP && !hasDP) {
      toast.info(t("admin.tenantEdit.noChanges", "No changes to save."));
      return;
    }

    // Mandatory reason — for MONEY changes only. Rent, deposit and discounts
    // are what COL-360 requires an explanation for; demanding one to correct a
    // phone number would be a workflow change to this shared panel that nobody
    // asked for, and would also attach an unrelated reason to a discount the
    // admin never touched.
    const moneyChanged =
      "monthly_rent" in bookingPatch ||
      "deposit_amount" in bookingPatch ||
      hasDP;
    if (moneyChanged && !reason.trim()) {
      toast.error(
        t(
          "admin.tenantEdit.errNeedReason",
          "Add a reason to save your changes."
        )
      );
      return;
    }

    // The DB update carries the reminder signal too, but keep it out of the
    // type-checked BookingRow patch.
    const bookingUpdate = {
      ...bookingPatch,
      ...reminderSignal,
      ...discountPatch,
      ...(hasDP && {
        discount_reason: reason.trim(),
        discount_set_at: new Date().toISOString(),
        discount_set_by: currentUserId,
      }),
    };

    try {
      // Reading a discount column as it will be AFTER this save. `in` rather
      // than `??`: this save may have set a column to null on purpose, and that
      // null is the new truth — `??` would fall back to the stored value and
      // reprice against a discount that no longer exists.
      const pickDiscountCol = (col: string) =>
        col in discountPatch
          ? discountPatch[col]
          : ((booking as unknown as Record<string, unknown>)[col] ?? null);

      const tasks: Promise<unknown>[] = [];
      if (hasBP || hasDP)
        tasks.push(
          updateBooking.mutateAsync(bookingUpdate as Partial<BookingRow>)
        );
      if (hasPP && profile?.id)
        tasks.push(updateProfile.mutateAsync(profilePatch));
      await Promise.all(tasks);

      // The contractual deposit moved — carry it onto the deposit payment row so
      // the two cannot disagree. They are separate writes, and the gap was
      // unrecoverable from the UI: `resolveDepositState` compares
      // bookings.deposit_amount against the SUM OF ROW AMOUNTS, so a row minted
      // at €0 (or at an older, lower amount) left the deposit permanently short
      // and the tenant parked at Deposit pending.
      //
      // Unpaid rows only. A paid row's amount is a record of money actually
      // received; rewriting it would falsify the ledger. And a single row only —
      // several live rows mean an installment plan, where the split is
      // deliberate and not ours to collapse.
      //
      // A deposit DISCOUNT moves the expected figure just as a changed
      // deposit_amount does, so it has to reach the row too (COL-360). Checkout
      // prefers the payment row's amount over the resolver, so a stale row here
      // means charging the tenant the undiscounted deposit while the booking and
      // the confirmation trigger both expect the discounted one.
      const depositDiscountChanged =
        "deposit_discount_value" in discountPatch ||
        "deposit_discount_mode" in discountPatch;
      // The room's own base deposit is a fallback tier `effectiveDepositEUR`
      // needs — omit it here and a booking with no deposit_amount of its own
      // reprices its deposit row against the stale 2x-rent fallback instead
      // of the base the room was actually configured with (COL-360).
      const roomForDeposit =
        booking.room_id != null &&
        ("deposit_amount" in bookingPatch || depositDiscountChanged)
          ? (
              await supabase
                .from("rooms")
                .select("id, price, deposit_amount")
                .eq("id", booking.room_id)
                .maybeSingle()
            ).data
          : null;
      if ("deposit_amount" in bookingPatch || depositDiscountChanged) {
        const expectedDepositEUR = effectiveDepositEUR({
          monthly_rent:
            "monthly_rent" in bookingPatch
              ? (bookingPatch.monthly_rent ?? null)
              : booking.monthly_rent,
          deposit_amount:
            "deposit_amount" in bookingPatch
              ? (bookingPatch.deposit_amount ?? null)
              : booking.deposit_amount,
          deposit_discount_mode: pickDiscountCol("deposit_discount_mode") as
            | string
            | null,
          deposit_discount_value: pickDiscountCol("deposit_discount_value") as
            | number
            | null,
          rooms: roomForDeposit
            ? { deposit_amount: roomForDeposit.deposit_amount ?? null }
            : null,
        });
        const cents = Math.round(expectedDepositEUR * 100);
        // A row already carrying a Stripe identity is excluded — that is what
        // Stripe was actually told to collect, and rewriting it locally would
        // desync the ledger from the real in-flight charge.
        const { data: liveDeposits } = await supabase
          .from("payments")
          .select(
            "id, amount_cents, status, stripe_payment_intent_id, stripe_charge_id"
          )
          .eq("booking_id", booking.id)
          .eq("type", "deposit")
          .not("status", "in", "(canceled,failed,paid)");
        if (
          liveDeposits?.length === 1 &&
          !liveDeposits[0].stripe_payment_intent_id &&
          !liveDeposits[0].stripe_charge_id
        ) {
          if (cents > 0) {
            const { error: depErr } = await supabase
              .from("payments")
              .update({ amount_cents: cents })
              .eq("id", liveDeposits[0].id);
            if (depErr) throw depErr;
          } else {
            // A 100% deposit discount resolves to 0 — nothing left to collect.
            // Leaving the row at its old positive amount would let checkout
            // (or any other collector reading it) still charge the tenant the
            // full pre-waiver deposit after the waiver was saved (COL-360).
            const { error: cancelErr } = await supabase
              .from("payments")
              .update({ status: "canceled" })
              .eq("id", liveDeposits[0].id);
            if (cancelErr) throw cancelErr;
          }
        }
      }

      // ── Base values → the room (COL-360) ────────────────────────────────
      //
      // The reported bug: this panel wrote only the booking, so the room — what
      // the listing, the room tabs and every property view show — kept the old
      // figure. AdminTenants' rent editor already did this; the arithmetic is
      // shared (resolveRoomBaseSync / repriceRentRows) so the two surfaces
      // cannot drift into billing different amounts for the same edit.
      //
      // Base values only. A tenant-specific discount never touches the room:
      // the unit keeps advertising its real price.
      let roomPatchApplied: Record<string, number> | null = null;
      // Only the tier that can actually edit the commercial fields syncs them.
      // A plain admin's save must never touch the room: their panel does not
      // render rent/deposit, so the state here is simply what was loaded.
      //
      // The form field holds a value on EVERY super-admin save, whether or not
      // this save actually changed it — so the room sync (and only the room
      // sync) is additionally gated on `"monthly_rent"/"deposit_amount" in
      // bookingPatch`, which bookingPatch only sets when the value genuinely
      // differs from what was stored. Without that, a discount-only or
      // unrelated-field save would silently overwrite the room's advertised
      // price/deposit with this booking's unchanged figures — e.g. if the
      // listing price legitimately moved since this tenant's agreement, or the
      // admin is editing a former tenant (COL-360 review).
      const rentActuallyChanged =
        isSuperAdmin && "monthly_rent" in bookingPatch;
      const depositActuallyChanged =
        isSuperAdmin && !depositLocked && "deposit_amount" in bookingPatch;
      const baseRentValue =
        !isSuperAdmin || monthlyRent === "" ? null : Number(monthlyRent);
      const baseDepositValue =
        !isSuperAdmin || depositLocked || depositAmount === ""
          ? null
          : Number(depositAmount);
      const baseRentForSync = rentActuallyChanged ? baseRentValue : null;
      const baseDepositForSync = depositActuallyChanged
        ? baseDepositValue
        : null;

      if (
        booking.room_id != null &&
        (baseRentForSync != null || baseDepositForSync != null)
      ) {
        const { data: roomRow } = await supabase
          .from("rooms")
          .select("id, price, deposit_amount")
          .eq("id", booking.room_id)
          .maybeSingle();

        const roomPatch = resolveRoomBaseSync({
          currentRoomPrice: roomRow?.price ?? null,
          currentRoomDeposit: roomRow?.deposit_amount ?? null,
          nextBaseRent: baseRentForSync,
          nextBaseDeposit: baseDepositForSync,
        });

        if (roomPatch) {
          const { error: roomErr } = await supabase
            .from("rooms")
            .update(roomPatch)
            .eq("id", booking.room_id);
          if (roomErr) throw roomErr;
          roomPatchApplied = roomPatch as Record<string, number>;
        }
      }

      // Unpaid rent rows follow the new base and the discount window. Paid rows
      // are a record of money that arrived and are never rewritten.
      //
      // Unlike the room sync above, this must fire on a rent-DISCOUNT-only
      // edit too — repriceRentRows is what makes the discount actually reach
      // an already-materialized invoice, not just future ones. It uses
      // baseRentValue (the form's current base rent, changed or not) rather
      // than baseRentForSync, since a discount-only save has nothing in
      // bookingPatch.monthly_rent to key off.
      const rentDiscountActuallyChanged = [
        "rent_discount_mode",
        "rent_discount_value",
        "rent_discount_months",
        "rent_discount_start_month",
      ].some((col) => col in discountPatch);

      if (
        baseRentValue != null &&
        baseRentValue > 0 &&
        (rentActuallyChanged || rentDiscountActuallyChanged)
      ) {
        const { data: liveRentRows } = await supabase
          .from("payments")
          .select("id, due_date, amount_cents, status")
          .eq("booking_id", booking.id)
          .eq("type", "rent")
          .in("status", ["pending", "scheduled", "overdue", "failed"]);

        const stored = booking as unknown as Record<string, unknown>;
        const repriced = repriceRentRows({
          rows: liveRentRows ?? [],
          fullMonthlyRent: baseRentValue,
          moveInIso:
            booking.actual_move_in_date ??
            booking.move_in_date ??
            booking.start_date,
          firstMonthOverride:
            stored.first_month_rent_override != null
              ? Number(stored.first_month_rent_override)
              : null,
          // An explicitly nulled column means the discount was just REMOVED.
          // Reading these with `??` would treat that null as "absent" and fall
          // back to the stored value, repricing rows with a discount that no
          // longer exists and leaving those invoices undercharged.
          discount: {
            rent_discount_mode: pickDiscountCol("rent_discount_mode") as
              | string
              | null,
            rent_discount_value: pickDiscountCol("rent_discount_value") as
              | number
              | null,
            rent_discount_months: pickDiscountCol("rent_discount_months") as
              | number
              | null,
            rent_discount_start_month: pickDiscountCol(
              "rent_discount_start_month"
            ) as string | null,
          },
        });
        for (const row of repriced) {
          // Aborting on the first failure is the honest outcome: repricing some
          // rows and reporting success would leave the ledger half-migrated
          // with nothing to say so.
          const { error: rowErr } = await supabase
            .from("payments")
            .update({ amount_cents: row.amountCents })
            .eq("id", row.id);
          if (rowErr) throw rowErr;
        }
      }

      // ── Audit trail ─────────────────────────────────────────────────────
      //
      // One row per meaningful event rather than a single blob, because the
      // timeline renders them separately: a base change that moved the room has
      // a different blast radius from a discount that did not.
      if (hasDP) {
        const removed =
          discountPatch.rent_discount_value === null &&
          discountPatch.deposit_discount_value === null;
        await supabase.from("audit_logs").insert({
          user_id: currentUserId,
          action: removed ? "tenant_discount_removed" : "tenant_discount_set",
          resource_type: "booking",
          resource_id: String(booking.id),
          old_values: {
            rent_discount_value: booking.rent_discount_value ?? null,
            rent_discount_months: booking.rent_discount_months ?? null,
            deposit_discount_value: booking.deposit_discount_value ?? null,
          },
          new_values: discountPatch,
          metadata: {
            source: "admin_manual_override",
            ...(reason.trim() && { reason: reason.trim() }),
          },
        });
      }

      if (roomPatchApplied) {
        await supabase.from("audit_logs").insert({
          user_id: currentUserId,
          action: "room_base_synced_from_tenant",
          resource_type: "room",
          resource_id: String(booking.room_id),
          old_values: {},
          new_values: roomPatchApplied,
          metadata: {
            source: "admin_manual_override",
            reason: reason.trim(),
            booking_id: booking.id,
          },
        });
      }

      if (hasBP || hasPP) {
        await supabase.from("audit_logs").insert({
          user_id: currentUserId,
          action: "manual_override",
          resource_type: "booking",
          resource_id: String(booking.id),
          old_values: {
            ...(hasBP && {
              status: booking.status,
              contract_status: booking.contract_status,
              payment_status: booking.payment_status,
              move_in_date: booking.move_in_date,
              actual_move_in_date: booking.actual_move_in_date,
              move_out_date: booking.move_out_date,
              actual_move_out_date: booking.actual_move_out_date,
              monthly_rent: booking.monthly_rent,
              deposit_amount: booking.deposit_amount,
              name: booking.name,
            }),
            ...(hasPP && {
              full_name: profile?.full_name,
              name: profile?.name,
              email: profile?.email,
              phone: profile?.phone,
              date_of_birth: profile?.date_of_birth,
            }),
          },
          new_values: { ...bookingUpdate, ...profilePatch },
          metadata: {
            source: "admin_manual_override",
            ...(reason.trim() && { reason: reason.trim() }),
          },
        });
      }

      toast.success(t("admin.tenantEdit.changesSaved", "Changes saved."));
      onSaved?.();
      onClose();
    } catch (e: unknown) {
      toast.error(
        e instanceof Error
          ? e.message
          : t("admin.tenantEdit.errFailedSave", "Failed to save.")
      );
    }
  };

  const busy = updateBooking.isPending || updateProfile.isPending;

  /**
   * Is a reason required to save right now? Only money moves demand one, so the
   * label tells the truth instead of asking for an explanation of a typo fix.
   */
  const reasonRequired = (() => {
    if (!isSuperAdmin) return false;
    const rentNow = monthlyRent === "" ? null : Number(monthlyRent);
    if (rentNow !== (booking.monthly_rent ?? null)) return true;
    const depositNow = depositAmount === "" ? null : Number(depositAmount);
    if (!depositLocked && depositNow !== (booking.deposit_amount ?? null)) {
      return true;
    }
    return (
      JSON.stringify(discount) !==
      JSON.stringify(discountValueFromBooking(booking))
    );
  })();

  return (
    <Card className="border-2 border-amber-400/70 bg-amber-50/30 dark:bg-amber-950/20 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-amber-200/60 dark:border-amber-800/40">
        <span className="text-xs font-bold tracking-widest text-amber-700 dark:text-amber-400 uppercase">
          {t("admin.tenantEdit.manualOverride", "Manual Override")}
        </span>
        <span className="text-xs text-muted-foreground">
          {t("admin.tenantEdit.auditLogged", "Changes are audit-logged")}
        </span>
      </div>

      <div className="p-5 space-y-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="sa-name" className="text-xs font-medium">
              {t("admin.tenantEdit.name", "Name")}
            </Label>
            <Input
              id="sa-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-9 rounded-lg text-sm"
              placeholder={t("admin.tenantEdit.fullName", "Full name")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sa-email" className="text-xs font-medium">
              {t("admin.tenantEdit.email", "Email")}
            </Label>
            <Input
              id="sa-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-9 rounded-lg text-sm"
              placeholder="tenant@example.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sa-phone" className="text-xs font-medium">
              {t("admin.tenantEdit.phone", "Phone")}
            </Label>
            <Input
              id="sa-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-9 rounded-lg text-sm"
              placeholder="+49 89 123 456"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sa-dob" className="text-xs font-medium">
              {t("admin.tenantEdit.dob", "Date of birth")}
            </Label>
            <Input
              id="sa-dob"
              type="date"
              value={dob}
              max={latestAdultBirthDate()}
              onChange={(e) => setDob(e.target.value)}
              className="h-9 rounded-lg text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sa-gender" className="text-xs font-medium">
              {t("admin.tenantEdit.gender", "Gender")}
            </Label>
            <Select
              value={gender || "unset"}
              onValueChange={(v) => setGender(v === "unset" ? "" : v)}
            >
              <SelectTrigger id="sa-gender" className="h-9 rounded-lg text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">
                  {t("admin.tenantEdit.genderUnset", "Not specified")}
                </SelectItem>
                <SelectItem value="female">
                  {t("admin.tenantEdit.genderFemale", "Woman")}
                </SelectItem>
                <SelectItem value="male">
                  {t("admin.tenantEdit.genderMale", "Man")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sa-nationality" className="text-xs font-medium">
              {t("admin.tenantEdit.nationality", "Nationality")}
            </Label>
            <NationalityCombobox
              id="sa-nationality"
              value={nationality}
              onChange={setNationality}
              language={language}
              placeholder={t("admin.tenantEdit.notSpecified", "Not specified")}
              searchPlaceholder={t(
                "admin.tenantEdit.nationalitySearch",
                "Search country"
              )}
              emptyText={t(
                "admin.tenantEdit.nationalityEmpty",
                "No country found"
              )}
              clearLabel={t("admin.tenantEdit.notSpecified", "Not specified")}
              className="h-9 rounded-lg text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sa-jobcenter" className="text-xs font-medium">
              {t("admin.tenantEdit.jobcenter", "Jobcenter pays rent")}
            </Label>
            <Select
              value={jobcenter}
              onValueChange={(v) => setJobcenter(v as "unset" | "yes" | "no")}
            >
              <SelectTrigger
                id="sa-jobcenter"
                className="h-9 rounded-lg text-sm"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">
                  {t("admin.tenantEdit.notSpecified", "Not specified")}
                </SelectItem>
                <SelectItem value="yes">
                  {t("admin.tenantEdit.jobcenterYes", "Yes")}
                </SelectItem>
                <SelectItem value="no">
                  {t("admin.tenantEdit.jobcenterNo", "No")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator className="border-amber-200/60 dark:border-amber-800/40" />

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium flex items-center gap-1.5">
              {t("admin.tenantEdit.bookingStatus", "Booking status")}
              {stageBusy && (
                <Loader2 className="h-3 w-3 animate-spin text-teal-500" />
              )}
            </Label>
            <Select
              value={currentStageId}
              disabled={stageBusy}
              onValueChange={(v) => {
                const next = v as TenantJourneyStageId;
                if (next === currentStageId) return;
                onForceStage(next);
              }}
            >
              <SelectTrigger className="h-9 rounded-lg text-sm">
                <SelectValue
                  placeholder={t(
                    "admin.tenantEdit.selectPlaceholder",
                    "Select…"
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {TENANT_JOURNEY_STAGES.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {tenantJourneyStageLabel(t, s.id)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] leading-snug text-muted-foreground">
              {t(
                "admin.tenantEdit.appliesImmediately",
                "Applies immediately — sets the records needed to reach this stage."
              )}
            </p>
          </div>

          {isSuperAdmin && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  {t("admin.tenantEdit.contractStatus", "Contract status")}
                </Label>
                <Select
                  value={contractStatus}
                  onValueChange={setContractStatus}
                >
                  <SelectTrigger className="h-9 rounded-lg text-sm">
                    <SelectValue
                      placeholder={t(
                        "admin.tenantEdit.selectPlaceholder",
                        "Select…"
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRACT_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  {t("admin.tenantEdit.paymentStatus", "Payment status")}
                </Label>
                <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                  <SelectTrigger className="h-9 rounded-lg text-sm focus-visible:ring-teal-500 data-[state=open]:ring-teal-500">
                    <SelectValue
                      placeholder={t(
                        "admin.tenantEdit.selectPlaceholder",
                        "Select…"
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        {isSuperAdmin && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="sa-rent" className="text-xs font-medium">
                {t("admin.tenantEdit.monthlyRent", "Monthly rent (€)")}
              </Label>
              <Input
                id="sa-rent"
                type="number"
                min="0"
                step="0.01"
                value={monthlyRent}
                onChange={(e) => setMonthlyRent(e.target.value)}
                className="h-9 rounded-lg text-sm"
                placeholder="0.00"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sa-deposit" className="text-xs font-medium">
                {t("admin.tenantEdit.deposit", "Deposit (€)")}
              </Label>
              <Input
                id="sa-deposit"
                type="number"
                min="0"
                step="0.01"
                value={depositAmount}
                disabled={depositLocked}
                onChange={(e) => setDepositAmount(e.target.value)}
                className="h-9 rounded-lg text-sm"
                placeholder="0.00"
              />
              {depositLocked && (
                <p className="text-[11px] text-muted-foreground">
                  {t(
                    "admin.tenantEdit.depositSettledNote",
                    "Deposit is settled — only rent can be changed."
                  )}
                </p>
              )}
            </div>
          </div>
        )}

        {isSuperAdmin && (
          <TenantDiscountFields
            value={discount}
            onChange={setDiscount}
            baseRent={monthlyRent === "" ? 0 : Number(monthlyRent)}
            baseDeposit={depositAmount === "" ? 0 : Number(depositAmount)}
            startMonth={discountStartMonth}
            depositLocked={depositLocked}
          />
        )}

        {isSuperAdmin && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <Label
                htmlFor="sa-planned-move-in"
                className="text-xs font-medium"
              >
                {t("admin.tenantEdit.plannedMoveIn", "Planned move-in date")}
              </Label>
              <Input
                id="sa-planned-move-in"
                type="date"
                value={plannedMoveIn}
                onChange={(e) => setPlannedMoveIn(e.target.value)}
                className="h-9 rounded-lg text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor="sa-actual-move-in"
                className="text-xs font-medium"
              >
                {t("admin.tenantEdit.actualMoveIn", "Actual move-in date")}
              </Label>
              <Input
                id="sa-actual-move-in"
                type="date"
                value={actualMoveIn}
                onChange={(e) => setActualMoveIn(e.target.value)}
                className="h-9 rounded-lg text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor="sa-planned-move-out"
                className="text-xs font-medium"
              >
                {t("admin.tenantEdit.plannedMoveOut", "Planned move-out date")}
              </Label>
              <Input
                id="sa-planned-move-out"
                type="date"
                value={plannedMoveOut}
                onChange={(e) => setPlannedMoveOut(e.target.value)}
                className="h-9 rounded-lg text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor="sa-actual-move-out"
                className="text-xs font-medium"
              >
                {t("admin.tenantEdit.actualMoveOut", "Actual move-out date")}
              </Label>
              <Input
                id="sa-actual-move-out"
                type="date"
                value={actualMoveOut}
                onChange={(e) => setActualMoveOut(e.target.value)}
                className="h-9 rounded-lg text-sm"
              />
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="sa-reason" className="text-xs font-medium">
            {t("admin.tenantEdit.reasonForChange", "Reason for change")}{" "}
            {reasonRequired ? (
              <span className="font-normal text-destructive">
                {t("admin.tenantEdit.required", "required")}
              </span>
            ) : (
              <span className="font-normal text-muted-foreground">
                {t("admin.tenantEdit.optional", "optional")}
              </span>
            )}
          </Label>
          <Textarea
            id="sa-reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="rounded-lg text-sm"
            placeholder={t(
              "admin.tenantEdit.reasonPlaceholder",
              "Agreed goodwill discount for delayed move-in"
            )}
          />
          <p className="text-[11px] text-muted-foreground">
            {t(
              "admin.tenantEdit.reasonNote",
              "Required for rent, deposit and discount changes. Saved to the activity timeline with your name — admin-facing only, never shown to the tenant or owner, and never emailed."
            )}
          </p>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <Button
            type="button"
            size="sm"
            className="rounded-lg gap-2 bg-teal-500 hover:bg-teal-600 text-white"
            disabled={busy}
            onClick={() => void handleSave()}
          >
            <Save className="h-3.5 w-3.5" />
            {busy
              ? t("admin.tenantEdit.saving", "Saving…")
              : t("admin.tenantEdit.saveChanges", "Save changes")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="rounded-lg gap-1.5"
            disabled={busy}
            onClick={onClose}
          >
            <X className="h-3.5 w-3.5" />
            {t("admin.tenantEdit.cancel", "Cancel")}
          </Button>
        </div>
      </div>
    </Card>
  );
}
